import type { SessionConfigSelect } from '@agentclientprotocol/sdk/experimental/v2'
import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import type { AppConfiguration, SessionRecord } from '@web/api/types'
import { Sheet } from '@web/components/mobile/Sheet'
import { SessionCost } from '@web/components/SessionCost'
import { Icon } from '@web/components/ui/icons'
import { PillMenu } from '@web/components/ui/PillMenu'
import {
  configChoice,
  configLabel,
  PERMISSIONS,
} from '@web/components/ui/pillCatalog'
import { executionTitle } from '@web/lib/executionMode'
import { agentMetadata } from '@web/state/acp'
import { useConfig } from '@web/state/config-context'
import { useDemoScenarios } from '@web/state/demo-scenarios'
import { describeWork } from '@web/state/working'
import { type ReactNode, useEffect, useRef, useState } from 'react'

function selectChoices(option?: SessionConfigSelect) {
  return (option?.options ?? []).flatMap((item) =>
    'options' in item ? item.options : [item],
  )
}

export type SessionActions = {
  fork: () => void
  sleep: () => void
  resume: () => void
  busy: string | null
  terminal: boolean
}
/** Negotiated ACP configuration and capabilities determine available controls. */
export function StatusBar({
  actions,
  configuration,
  record,
  presentation = 'bar',
  open = false,
  onClose = () => {},
}: {
  actions: SessionActions
  configuration: AppConfiguration
  record: SessionRecord | null
  /** `bar` is the desktop footer; `sheet` is the phone's "Session settings" bottom sheet. */
  presentation?: 'bar' | 'sheet'
  open?: boolean
  onClose?: () => void
}) {
  const { snapshot, sessionId, interrupt, deferStartupOption, reportError } =
    useBridge()
  const catalog = useConfig()
  const { acp, bridge, socket } = snapshot
  const current = bridge.record ?? record
  const [changing, setChanging] = useState<string | null>(null)
  const changeSequence = useRef(0)
  const [stagedOptions, setStagedOptions] = useState<
    Record<string, string | boolean>
  >({})
  const work = describeWork(
    { ...acp, running: acp.running || !!snapshot.optimisticWorking },
    bridge,
  )
  const uiOnly = configuration.ui.mode === 'ui'
  const readOnly = current?.migration_status === 'diagnostic'
  const live =
    !readOnly &&
    !snapshot.hydrating &&
    !actions.terminal &&
    bridge.status !== 'starting' &&
    socket === 'open'
  const starting = bridge.status === 'starting'
  // Startup choices are retained in the local outbox, so they remain usable
  // before the socket has finished connecting.
  const configurable = live || (!readOnly && starting)
  const waiting = !starting && Object.keys(stagedOptions).length > 0
  const commandError = bridge.commandErrors.at(-1)
  const scenario = current?.config.startup_values?.scenario_id
  useEffect(() => {
    setStagedOptions((pending) => {
      const remaining = Object.fromEntries(
        Object.entries(pending).filter(([key, value]) =>
          key === '__scenario'
            ? scenario !== value
            : key === 'permission_mode'
              ? current?.config.permission_mode !== value
              : !acp.configOptions.some(
                  (option) =>
                    option.configId === key && option.currentValue === value,
                ),
        ),
      )
      return Object.keys(remaining).length === Object.keys(pending).length
        ? pending
        : remaining
    })
  }, [acp.configOptions, scenario, current?.config.permission_mode])
  useEffect(() => {
    if (
      commandError &&
      commandError.seq > changeSequence.current &&
      commandError.command === 'set_config_option'
    )
      setStagedOptions({})
  }, [commandError])
  useEffect(() => {
    if (!waiting) return
    const timer = setTimeout(() => {
      setStagedOptions({})
      reportError(
        'The setting change was not confirmed. Showing the last confirmed values; try again.',
      )
    }, 30_000)
    return () => clearTimeout(timer)
  }, [waiting, reportError])
  const setOption = (configId: string, value: string | boolean) => {
    if (starting) {
      return Promise.resolve(deferStartupOption(configId, value))
    }
    return api.command(sessionId, {
      type: 'set_config_option',
      config_id: configId,
      value,
    })
  }
  const scenarios = useDemoScenarios(
    current?.app_id ?? '',
    current?.config.package_digest,
    !!configuration.demo && !!current,
  )
  const metadata = agentMetadata(acp)
  const update = async (
    id: string,
    action: () => Promise<unknown>,
    choices: Record<string, string | boolean>,
  ) => {
    changeSequence.current = snapshot.lastSeq
    setStagedOptions((current) => ({ ...current, ...choices }))
    setChanging(id)
    try {
      await action()
    } catch (failure) {
      setStagedOptions({})
      reportError((failure as Error).message, id)
    } finally {
      setChanging(null)
    }
  }
  const sheet = presentation === 'sheet'
  /** In the sheet each control sits on its own labelled row; in the bar it is a bare pill. */
  const option = (
    key: string,
    label: string,
    control: ReactNode,
    description?: string,
    toggle = false,
  ) =>
    sheet ? (
      <div key={key} className={`m-option-row ${toggle ? 'is-toggle' : ''}`}>
        <span className="m-option-label">
          {label}
          {description && <small>{description}</small>}
        </span>
        {control}
      </div>
    ) : (
      <span key={key} className="contents">
        {control}
      </span>
    )
  const scenarioControl = configuration.demo && scenarios.catalog && (
    <PillMenu
      label="Scenario"
      showLabel={false}
      valueLabel={sheet ? 'full' : 'short'}
      pending={waiting && '__scenario' in stagedOptions}
      value={String(
        stagedOptions.__scenario ??
          current?.config.startup_values?.scenario_id ??
          scenarios.catalog.default_scenario,
      )}
      options={scenarios.catalog.scenarios.map((scenario) => ({
        value: scenario.id,
        label: scenario.title,
        description: scenario.description,
        icon: 'play',
      }))}
      disabled={!live || !!changing || waiting}
      onChange={(value) =>
        void update('scenario', () => api.changeScenario(sessionId, value), {
          __scenario: value,
        })
      }
    />
  )
  const permissionOption = acp.configOptions.find(
    (item) => item.configId === 'permission_mode' && item.type === 'select',
  ) as SessionConfigSelect | undefined
  const automaticControl = !uiOnly && (
    <PillMenu
      label="Approval Mode"
      showLabel={false}
      valueLabel={sheet ? 'full' : 'short'}
      value={String(
        stagedOptions.permission_mode ??
          current?.config.permission_mode ??
          'auto',
      )}
      options={
        permissionOption
          ? selectChoices(permissionOption).map((choice) =>
              configChoice('permission_mode', choice.value, choice.name),
            )
          : Object.entries(PERMISSIONS).map(([value, option]) => ({
              value,
              ...option,
            }))
      }
      disabled={!configurable || !!changing || waiting}
      pending={waiting && 'permission_mode' in stagedOptions}
      onChange={(value) =>
        void update(
          'permission_mode',
          () => setOption('permission_mode', value),
          { permission_mode: value },
        )
      }
    />
  )
  const optionControls = uiOnly
    ? []
    : acp.configOptions
        .filter(
          (item) =>
            ![
              'provider',
              'fast_mode',
              'permission_mode',
              'approval_policy',
            ].includes(item.configId),
        )
        .map((item) => {
          if (item.type === 'boolean') {
            const checkbox = (
              <input
                type="checkbox"
                aria-label={sheet ? item.name : undefined}
                checked={
                  (stagedOptions[item.configId] ?? item.currentValue) === true
                }
                disabled={!configurable || !!changing || waiting}
                onChange={(event) =>
                  void update(
                    item.configId,
                    () => setOption(item.configId, event.target.checked),
                    { [item.configId]: event.target.checked },
                  )
                }
              />
            )
            return sheet
              ? option(
                  item.configId,
                  item.name,
                  checkbox,
                  item.description ?? undefined,
                  true,
                )
              : option(
                  item.configId,
                  item.name,
                  <label className="chip">
                    {checkbox} {item.name}
                  </label>,
                )
          }
          if (item.type !== 'select') return null
          const choices = selectChoices(item as SessionConfigSelect)
          return option(
            item.configId,
            configLabel(item.configId, item.name),
            <PillMenu
              label={configLabel(item.configId, item.name)}
              showLabel={false}
              valueLabel={sheet ? 'full' : 'short'}
              pending={waiting && item.configId in stagedOptions}
              value={String(stagedOptions[item.configId] ?? item.currentValue)}
              options={choices.map((choice) =>
                configChoice(
                  item.configId,
                  choice.value,
                  choice.name,
                  choice.description ?? undefined,
                  catalog,
                ),
              )}
              disabled={!configurable || !!changing || waiting}
              onChange={(value) =>
                void update(
                  item.configId,
                  () => setOption(item.configId, value),
                  { [item.configId]: value },
                )
              }
            />,
            item.description ?? undefined,
          )
        })
  const lifecycle = (
    <span className="session-lifecycle" title={work.detail ?? undefined}>
      <span className={`session-lifecycle-dot tone-${work.tone}`} />
      {work.label}
    </span>
  )
  const cost = <SessionCost sessionId={sessionId} total={bridge.totalCostUsd} />
  const chips = (
    <>
      {metadata.executionMode === 'claude-managed' && (
        <span className="chip">
          {executionTitle('claude-managed', bridge.record?.config.provider)}
        </span>
      )}
      {acp.usage && acp.usage.size > 0 && (
        <span
          className="chip session-context"
          title={`${acp.usage.used.toLocaleString()} of ${acp.usage.size.toLocaleString()} context tokens`}
        >
          {Math.round((acp.usage.used / acp.usage.size) * 100)}% context
        </span>
      )}
    </>
  )
  const connection = (
    <span
      aria-label={`Connection ${socket}`}
      className={socket === 'open' ? 'text-ok' : 'text-warn'}
    >
      <Icon name={socket === 'open' ? 'online' : 'offline'} size={13} />
    </span>
  )
  const showFork =
    !uiOnly &&
    current?.config.execution_mode === 'docker' &&
    !!acp.capabilities.session?.fork
  const stopButton = !uiOnly && acp.running && (
    <button
      className="btn btn-sm btn-danger"
      aria-label={
        bridge.stopping ? 'Stopping current turn' : 'Stop current turn'
      }
      disabled={!live || bridge.stopping}
      onClick={interrupt}
    >
      <Icon name={bridge.stopping ? 'loader' : 'stop'} size={10} />
      {bridge.stopping ? 'Stopping…' : 'Stop'}
    </button>
  )
  const sleepButton = (
    <button
      className="btn btn-sm"
      disabled={
        readOnly ||
        snapshot.hydrating ||
        !!actions.busy ||
        (!actions.terminal && !live)
      }
      onClick={actions.terminal ? actions.resume : actions.sleep}
    >
      <Icon
        name={actions.busy ? 'loader' : actions.terminal ? 'play' : 'clock'}
        size={12}
        className={actions.busy ? 'spin' : ''}
      />
      {actions.terminal ? 'Resume' : 'Sleep'}
    </button>
  )
  if (sheet)
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title="Session settings"
        eyebrow={current?.title ?? undefined}
        className="m-session-settings"
        testId="session-settings-sheet"
      >
        <div
          className="m-status-line"
          role="status"
          aria-label={`Session ${work.label}`}
        >
          {lifecycle}
          {cost}
          {socket !== 'open' && connection}
          <span className="m-status-spacer" />
          {stopButton}
          {sleepButton}
        </div>
        <div className="m-option-list">
          {scenarioControl && option('scenario', 'Scenario', scenarioControl)}
          {automaticControl &&
            option('approval', 'Approval Mode', automaticControl)}
          {optionControls}
        </div>
        {(metadata.executionMode === 'claude-managed' ||
          (acp.usage && acp.usage.size > 0)) && (
          <div className="m-chips">{chips}</div>
        )}
        {showFork && (
          <button
            type="button"
            className="m-text-action"
            disabled={!live}
            onClick={() => {
              onClose()
              actions.fork()
            }}
          >
            <Icon name="fork" size={14} />
            Fork this session
          </button>
        )}
        {starting ? (
          <p className="m-hint">
            Changes made now apply as soon as the session is ready.
          </p>
        ) : !live ? (
          <p className="m-hint">
            Settings can change once the session is connected and idle.
          </p>
        ) : null}
      </Sheet>
    )
  return (
    <footer
      className="session-status chrome"
      role="contentinfo"
      aria-label="Session status bar"
    >
      <div className="session-status-primary">
        {lifecycle}
        {cost}
      </div>
      <div className="session-status-options">
        {scenarioControl}
        {automaticControl}
        {optionControls}
        {chips}
        {connection}
      </div>
      <div className="session-status-actions">
        {showFork && (
          <button
            className="btn btn-sm session-fork"
            disabled={!live}
            onClick={actions.fork}
          >
            <Icon name="fork" size={11} />
            Fork
          </button>
        )}
        {stopButton}
        {sleepButton}
      </div>
    </footer>
  )
}
